import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Zap, ArrowRight, Target, Mic, Sparkles, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import RefinementSection from '../components/RefinementSection';
import EmotionCheckpoint from '../components/EmotionCheckpoint';

// ============ CONTENT DIRECTION EXPERIENCE ============
// This is ACTIVATION, not introspection.
// "I know what to do now."

// Loading messages - more action-oriented
const LOADING_STAGES = [
  { text: "Nous analysons ton positionnement...", duration: 4000 },
  { text: "Nous définissons tes piliers de contenu...", duration: 6000 },
  { text: "Nous créons ta direction...", duration: 8000 },
  { text: "Finalisation...", duration: null }
];

const ContentLoadingExperience = ({ loadingStage }) => {
  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        {/* Dynamic icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative w-20 h-20 mx-auto mb-10"
        >
          {/* Outer pulse - faster than Vision */}
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0, 0.2] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
            className="absolute inset-0 bg-[#D4AF37]/10 rounded-full"
          />
          {/* Inner circle */}
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="absolute inset-2 border border-[#D4AF37]/40 rounded-full flex items-center justify-center bg-[#D4AF37]/5"
          >
            <Zap className="text-[#D4AF37]" size={28} />
          </motion.div>
        </motion.div>
        
        {/* Progress bar style */}
        <div className="w-48 h-1 mx-auto mb-8 bg-[#1C1C1C] rounded-full overflow-hidden">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: `${((loadingStage + 1) / LOADING_STAGES.length) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-[#D4AF37]/50 to-[#D4AF37] rounded-full"
          />
        </div>
        
        {/* Animated text */}
        <AnimatePresence mode="wait">
          <motion.p
            key={loadingStage}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="text-white text-base mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {LOADING_STAGES[loadingStage]?.text}
          </motion.p>
        </AnimatePresence>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-[#52525B] text-xs"
        >
          Bientôt prête à agir
        </motion.p>
      </div>
    </div>
  );
};

const ContentRevelation = () => {
  const [stage, setStage] = useState(0); // 0: loading, 1-6: stages
  const [contentData, setContentData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [loadingStage, setLoadingStage] = useState(0);
  const [error, setError] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  const [refinedContent, setRefinedContent] = useState(null);
  const [showRefinementMode, setShowRefinementMode] = useState(false);
  const [showEmotionCheckpoint, setShowEmotionCheckpoint] = useState(false);
  
  const { token, user, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Determine if already completed (after auth loads) - but allow refinement mode
  const isAlreadyCompleted = !authLoading && user?.content_revelation_completed === true && !showRefinementMode;

  // Progressive loading stages (only if not already completed)
  useEffect(() => {
    if (!isGenerating || isAlreadyCompleted || authLoading) return;
    
    const timers = [];
    let currentTime = 0;
    
    LOADING_STAGES.forEach((stage, index) => {
      if (stage.duration && index < LOADING_STAGES.length - 1) {
        currentTime += stage.duration;
        const timer = setTimeout(() => {
          setLoadingStage(index + 1);
        }, currentTime);
        timers.push(timer);
      }
    });
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [isGenerating, isAlreadyCompleted]);

  // Generate Content Direction on mount (only if not already completed)
  useEffect(() => {
    if (isAlreadyCompleted || authLoading) return;
    
    const generateContent = async () => {
      try {
        const response = await axios.post(
          `${API}/content/direction`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setContentData(response.data);
        setIsGenerating(false);
        // Refresh user to get updated flags
        if (refreshUser) refreshUser();
        setTimeout(() => setStage(1), 600);
      } catch (err) {
        console.error('Content direction error:', err);
        setError('Une erreur est survenue.');
        setIsGenerating(false);
      }
    };

    generateContent();
  }, [token, isAlreadyCompleted, refreshUser]);

  const nextStage = () => setStage(prev => prev + 1);
  
  const goToEmotionCheckpoint = () => {
    setShowEmotionCheckpoint(true);
  };
  
  const goToFirstAction = () => navigate('/first-action');
  const goToContentTools = () => navigate('/dashboard?tab=content');
  const goToRefinement = () => {
    setShowRefinementMode(true); // Enable refinement mode
  };
  
  // Refinement handler
  const handleRefine = async (notes) => {
    setIsRefining(true);
    try {
      const response = await axios.post(
        `${API}/content/direction/refine`,
        { refinement_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefinedContent(response.data);
      setContentData(response.data); // Update main content with refined version
    } catch (err) {
      console.error('Content refinement error:', err);
    }
    setIsRefining(false);
  };
  
  const continueJourney = () => {
    // Navigate to the next incomplete step
    if (!user?.first_action_completed) {
      navigate('/first-action');
    } else {
      navigate('/dashboard');
    }
  };

  // Continue journey but show emotion checkpoint first
  const continueWithCheckpoint = () => {
    setShowEmotionCheckpoint(true);
  };

  // Retry content generation
  const retryGeneration = () => {
    setError(null);
    setIsGenerating(true);
    setLoadingStage(0);
  };

  // Show loading while auth is loading
  if (authLoading) return <ContentLoadingExperience loadingStage={0} />;
  
  // Show standalone refinement screen when user clicked "Affiner ma direction"
  if (showRefinementMode) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="standalone-refinement"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <RefinementSection
              stepName="content"
              isRefining={isRefining}
              refinedData={refinedContent}
              onRefine={handleRefine}
              onContinue={goToFirstAction}
              onSkip={() => setShowRefinementMode(false)}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
  
  // Show "already completed" screen if step is done
  if (isAlreadyCompleted) {
    // Show Emotion Checkpoint if triggered
    if (showEmotionCheckpoint) {
      return (
        <EmotionCheckpoint
          step="content"
          onComplete={continueJourney}
        />
      );
    }
    
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="already-completed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="w-20 h-20 mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mb-6"
            >
              Étape complétée
            </motion.p>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white text-2xl md:text-4xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ta direction de contenu est déjà définie.
            </motion.h2>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-[#71717A] text-base text-center max-w-md mb-12"
            >
              Tu as déjà créé ta direction de contenu.
              <br />
              Que souhaites-tu faire maintenant ?
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="flex flex-col items-center gap-4"
            >
              <Button
                onClick={continueWithCheckpoint}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="continue-journey"
              >
                Continuer mon parcours <ArrowRight className="ml-3" size={18} />
              </Button>
              
              <Button
                onClick={goToRefinement}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="refine-content"
              >
                <Sparkles className="mr-2" size={16} />
                Affiner ma direction
              </Button>
              
              <Button
                onClick={goToContentTools}
                className="bg-transparent text-[#71717A] hover:text-white px-8 py-3"
                data-testid="create-more-content"
              >
                Créer plus de contenu <Zap className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // Loading experience
  if (isGenerating) {
    return <ContentLoadingExperience loadingStage={loadingStage} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-6 border border-red-400/30 rounded-full flex items-center justify-center">
            <Zap className="text-red-400" size={24} />
          </div>
          <p className="text-white text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Oups, une erreur s'est produite
          </p>
          <p className="text-[#A1A1AA] text-sm mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={retryGeneration} 
              className="bg-[#D4AF37] hover:bg-[#B8860B] text-black w-full"
              data-testid="content-retry-btn"
            >
              Réessayer <ArrowRight className="ml-2" size={16} />
            </Button>
            <button 
              onClick={() => navigate('/vision-revelation')}
              className="text-[#71717A] text-sm hover:text-white transition-colors"
            >
              Retourner à l'étape précédente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Extract pillars from content data
  const pillars = contentData?.what_to_talk_about || {};
  const howToShow = contentData?.how_to_show_up || {};
  const signatureIdea = contentData?.signature_content_idea || {};

  return (
    <div className="min-h-screen bg-[#030303] overflow-hidden">
      <AnimatePresence mode="wait">
        
        {/* ============ STAGE 1: TRANSITION FROM VISION ============ */}
        {stage === 1 && (
          <motion.div
            key="transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="w-16 h-16 mb-10 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Zap className="text-[#D4AF37]" size={26} />
            </motion.div>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-[#A1A1AA] text-base mb-4"
            >
              Tu sais qui tu deviens.
            </motion.p>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-white text-2xl md:text-4xl text-center max-w-2xl leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Maintenant, traduisons ça en action.
            </motion.h1>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="text-[#D4AF37] text-sm mt-6 tracking-wide"
            >
              Comment tu te montres.
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-8 py-5 font-medium"
                data-testid="content-continue"
              >
                C'est parti <ArrowRight className="ml-2" size={18} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 2: CONTENT PILLARS ============ */}
        {stage === 2 && (
          <motion.div
            key="pillars"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-8"
            >
              <Target className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.25em] uppercase">
                Ce dont tu parles
              </p>
            </motion.div>
            
            <div className="max-w-4xl w-full grid md:grid-cols-3 gap-6">
              {Object.entries(pillars).map(([key, pillar], index) => (
                <motion.div
                  key={key}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + (index * 0.2), duration: 0.5 }}
                  className="border border-[#27272A] p-6 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <p className="text-[#D4AF37] text-xs tracking-[0.15em] uppercase mb-3">
                    Pilier {index + 1}
                  </p>
                  <h3 
                    className="text-white text-lg mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {pillar?.theme}
                  </h3>
                  <p className="text-[#71717A] text-sm leading-relaxed">
                    {pillar?.why}
                  </p>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="content-next-2"
              >
                Suivant <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 3: HOW TO SHOW UP ============ */}
        {stage === 3 && (
          <motion.div
            key="showup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-10"
            >
              <Mic className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.25em] uppercase">
                Comment tu te montres
              </p>
            </motion.div>
            
            <div className="max-w-2xl w-full space-y-8">
              {/* Energy */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="flex gap-5"
              >
                <div className="w-1 bg-gradient-to-b from-[#D4AF37] to-transparent flex-shrink-0" />
                <div>
                  <p className="text-[#D4AF37] text-xs tracking-[0.15em] uppercase mb-2">Ton énergie</p>
                  <p className="text-white text-base leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {howToShow?.energy}
                  </p>
                </div>
              </motion.div>
              
              {/* Voice */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="flex gap-5"
              >
                <div className="w-1 bg-gradient-to-b from-[#D4AF37] to-transparent flex-shrink-0" />
                <div>
                  <p className="text-[#D4AF37] text-xs tracking-[0.15em] uppercase mb-2">Ta voix</p>
                  <p className="text-white text-base leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {howToShow?.voice}
                  </p>
                </div>
              </motion.div>
              
              {/* Frequency */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                className="flex gap-5"
              >
                <div className="w-1 bg-gradient-to-b from-[#D4AF37] to-transparent flex-shrink-0" />
                <div>
                  <p className="text-[#D4AF37] text-xs tracking-[0.15em] uppercase mb-2">Ton rythme</p>
                  <p className="text-white text-base leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {howToShow?.frequency_recommendation}
                  </p>
                </div>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="content-next-3"
              >
                Suivant <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 4: WHAT MAKES HER DIFFERENT ============ */}
        {stage === 4 && (
          <motion.div
            key="different"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-8"
            >
              <Sparkles className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.25em] uppercase">
                Ce qui te différencie
              </p>
            </motion.div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="max-w-3xl text-center mb-10"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {contentData?.content_positioning}
              </p>
            </motion.div>
            
            {/* What to avoid */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.4 }}
              className="max-w-xl w-full border-t border-[#27272A] pt-8 mt-4"
            >
              <p className="text-[#52525B] text-xs tracking-[0.15em] uppercase mb-4 text-center">
                Ce que tu évites
              </p>
              <div className="space-y-2">
                {contentData?.avoid?.slice(0, 2).map((item, index) => (
                  <p key={index} className="text-[#71717A] text-sm text-center">
                    — {item}
                  </p>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="content-next-4"
              >
                Suivant <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 5: SIGNATURE CONTENT IDEA ============ */}
        {stage === 5 && (
          <motion.div
            key="signature"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-6"
            >
              <Rocket className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.25em] uppercase">
                Ton contenu signature
              </p>
            </motion.div>
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="max-w-3xl text-center"
            >
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="w-16 h-px bg-[#D4AF37] mx-auto mb-8"
              />
              
              <h2 
                className="text-[#D4AF37] text-2xl md:text-3xl mb-6 italic"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                "{signatureIdea?.title}"
              </h2>
              
              <p className="text-[#E5E5E5] text-base leading-relaxed mb-8">
                {signatureIdea?.concept}
              </p>
              
              <div className="bg-[#0A0A0A] border border-[#1C1C1C] p-5 rounded-sm">
                <p className="text-[#52525B] text-xs tracking-[0.1em] uppercase mb-2">
                  Pourquoi ça marche
                </p>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  {signatureIdea?.why_it_works}
                </p>
              </div>
              
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="w-16 h-px bg-[#D4AF37] mx-auto mt-8"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.4 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="content-next-5"
              >
                Terminer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 6: CLOSING - ACTIVATION ============ */}
        {stage === 6 && (
          <motion.div
            key="closing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="w-20 h-20 mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Zap className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white text-2xl md:text-4xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu n'as pas besoin de faire plus.
            </motion.h2>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-[#D4AF37] text-xl text-center mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu as besoin de te montrer alignée.
            </motion.p>
            
            {/* Refinement Section - Co-creation feature */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="w-full"
            >
              <RefinementSection
                stepName="content"
                isRefining={isRefining}
                refinedData={refinedContent}
                onRefine={handleRefine}
                onContinue={goToEmotionCheckpoint}
                onSkip={goToEmotionCheckpoint}
              />
            </motion.div>
          </motion.div>
        )}

        {/* Emotion Checkpoint */}
        {showEmotionCheckpoint && (
          <EmotionCheckpoint
            step="content"
            onComplete={goToFirstAction}
          />
        )}
        
      </AnimatePresence>
    </div>
  );
};

export default ContentRevelation;
